// Redux
import { RootState } from '@redux/rootReducer';
// Hooks
import { useAppSelector } from '@hooks/redux';
// Models
import { CodesDocumentTypes, IValidateSalary } from '@models/ValidateSalary';
import { TypesDocumentIds } from '@pages/employees/constants/Employees';
import { InputType } from '@constants/Input';
import { VerificationDigitCalculator } from '@utils/verifitcationDigit';
import { NITValidationRegex } from '@utils/regex';
import { documentNumberValidationMessage } from '@constants/validationMessages';
import { ValidationMessage } from '@utils/Validations';

/**
 * Validate document number by document type id
 * @param {string | undefined} value - Current value document number
 * @param {string} documentTypeId - Id document type selected
 * @returns {IValidateSalary | object} error from validation
 */
export const useValidateDocumentNumber = (): {
    validateDocumentNumber: (value: string | undefined, documentTypeId: string) => IValidateSalary | object;
    getMaxLengthDocumentByType: (documentTypeId: string) => number;
    getInputDocumentTypeByDocumentTypeId: (documentTypeId: string) => string;
    handleValidateNitDocumentNumberChange: (
        e: React.ChangeEvent<HTMLInputElement>,
        documentTypeId: string,
        setFieldValue: (field: string, value: unknown) => void,
        documentNumberFieldName: string,
        verificationDigitFieldName: string
    ) => void;
} => {
    const { document_type } = useAppSelector((state: RootState) => state.utils);

    const validateDocumentWithLengthRange = (
        value: string | undefined,
        minLength: number,
        maxLength: number
    ): IValidateSalary => {
        const errors: IValidateSalary = {};
        if (!value) {
            errors.document_number = '* Campo requerido';
        } else if (value.startsWith('0')) {
            errors.document_number = documentNumberValidationMessage.cannotStartWith0;
        } else if (!new RegExp(`^\\d{${minLength},${maxLength}}$`).test(value)) {
            errors.document_number = `La longitud del número de identificación debe estar entre ${minLength} y ${maxLength} caracteres.`;
        }
        return errors;
    };

    const getDocumentTypeCodeById = (documentTypeId: string): string | undefined => {
        const normalizedDocumentTypeId = documentTypeId?.toLowerCase();
        const currentCode = document_type?.find(element => element.id?.toLowerCase() === normalizedDocumentTypeId)?.code;
        return currentCode ? String(currentCode) : undefined;
    };

    const validateDocumentNumber = (value: string | undefined, documentTypeId: string): IValidateSalary | object => {
        const currentCode = getDocumentTypeCodeById(documentTypeId);

        const documentNumberValue = parseInt(value ?? '0', 10);

        const isValidCCRange =
            (documentNumberValue >= 100000 && documentNumberValue <= 99999999) ||
            (documentNumberValue >= 1000000000 && documentNumberValue <= 9999999999);

        const errors: IValidateSalary = {};
        switch (currentCode) {
            case CodesDocumentTypes.CC:
                if (!value) {
                    return { ...errors, document_number: '* Campo requerido' };
                }
                if (value.startsWith('0')) {
                    return {
                        ...errors,
                        document_number: documentNumberValidationMessage.cannotStartWith0,
                    };
                }
                if (!/^(\d{6,8}|\d{10})$/.test(value)) {
                    return {
                        ...errors,
                        document_number: 'La longitud permitida debe estar entre 6 a 8 o 10 caracteres.',
                    };
                }
                if (!isValidCCRange) {
                    return {
                        ...errors,
                        document_number: 'El número registrado no está en el rango permitido',
                    };
                }

                return errors;
            case CodesDocumentTypes.CE:
                if (!value) {
                    errors.document_number = '* Campo requerido';
                } else if (value.startsWith('0')) {
                    errors.document_number = documentNumberValidationMessage.cannotStartWith0;
                } else if (!/^\d{3,7}$/.test(value)) {
                    errors.document_number = 'La longitud permitida debe estar entre 3 y 7 caracteres.';
                }
                return errors;

            case CodesDocumentTypes.NIT:
                if (!value) {
                    errors.document_number = '* Campo requerido';
                } else if (!/^[6789]/.test(value)) {
                    errors.document_number = 'El número de documento debe iniciar en 6, 7, 8 o 9';
                } else if (!/^[1-9]\d{8}$/i.test(value)) {
                    errors.document_number = 'La longitud permitida debe ser de 9 caracteres';
                }
                return errors;

            case CodesDocumentTypes.TI:
                return validateDocumentWithLengthRange(value, 10, 11);
            case CodesDocumentTypes.PA:
                if (!value) {
                    errors.document_number = '* Campo requerido';
                } else if (!/^[A-Za-zÑñ\d]{7,16}$/.test(value)) {
                    errors.document_number =
                        'El número de identificación sólo debe contener letras y números y su longitud debe estar entre 7 y 16 caracteres.';
                }

                return errors;
            case CodesDocumentTypes.CD:
                if (!value) {
                    errors.document_number = '* Campo requerido';
                } else if (!/^\d{1,11}$/.test(value)) {
                    errors.document_number =
                        'El número de identificación sólo debe contener números y su longitud debe estar entre 1 y 11 caracteres.';
                }
                return errors;

            case CodesDocumentTypes.SC:
                if (!value) {
                    errors.document_number = '* Campo requerido';
                } else if (!/^[A-Za-z0-9Ññ]{7,8}$/.test(value)) {
                    errors.document_number =
                        'El número de identificación sólo debe contener letras y números y su longitud debe estar entre 7 y 8 caracteres.';
                }
                return errors;

            case CodesDocumentTypes.PE:
                if (!value) {
                    errors.document_number = '* Campo requerido';
                } else if (!/^\d{15}$/i.test(value)) {
                    errors.document_number =
                        'El número de identificación sólo debe tener números y su longitud debe ser de 15 caracteres.';
                }
                return errors;
            case CodesDocumentTypes.PT:
                if (!value) {
                    errors.document_number = '* Campo requerido';
                } else if (value.startsWith('0')) {
                    errors.document_number = documentNumberValidationMessage.cannotStartWith0;
                } else if (!/^\d{2,8}$/.test(value)) {
                    errors.document_number =
                        'El número de identificación sólo debe contener números y su longitud debe estar entre 2 y 8 caracteres.';
                }
                return errors;

            case CodesDocumentTypes.RC:
                return validateDocumentWithLengthRange(value, 10, 11);
            case CodesDocumentTypes.PC:
                if (!value) {
                    errors.document_number = '* Campo requerido';
                } else if (!/^\d{1,8}$/.test(value)) {
                    errors.document_number =
                        'El número de identificación sólo debe contener números y su longitud debe estar entre 1 y 8 caracteres.';
                }
                return errors;

            default:
                if (!documentTypeId) {
                    errors.document_type_id = ValidationMessage.required;
                } else if (!value) {
                    errors.document_number = ValidationMessage.required;
                }
                return errors;
        }
    };

    const getMaxLengthDocumentByType = (documentTypeId: string): number => {
        const documentMaxLengthMap: Record<string, number> = {
            [TypesDocumentIds.CC]: 10,
            [TypesDocumentIds.NIT]: 9,
            [TypesDocumentIds.CE]: 7,
            [TypesDocumentIds.TI]: 11,
            [TypesDocumentIds.RC]: 11,
            [TypesDocumentIds.PA]: 16,
            [TypesDocumentIds.CD]: 11,
            [TypesDocumentIds.SC]: 8,
            [TypesDocumentIds.PE]: 15,
            [TypesDocumentIds.PT]: 8,
            [TypesDocumentIds.PC]: 8,
        };

        return documentMaxLengthMap[documentTypeId] ?? documentMaxLengthMap[documentTypeId?.toUpperCase()] ?? 10;
    };

    const getInputDocumentTypeByDocumentTypeId = (documentTypeId: string): string => {
        const inputDocumentTypehMap: Record<string, string> = {
            [TypesDocumentIds.CC]: 'number',
            [TypesDocumentIds.NIT]: 'number',
            [TypesDocumentIds.CE]: 'number',
            [TypesDocumentIds.TI]: 'number',
            [TypesDocumentIds.RC]: 'number',
            [TypesDocumentIds.PA]: InputType.ALPHANUMERIC,
            [TypesDocumentIds.CD]: 'number',
            [TypesDocumentIds.SC]: InputType.ALPHANUMERIC,
            [TypesDocumentIds.PE]: 'number',
            [TypesDocumentIds.PT]: 'number',
            [TypesDocumentIds.PC]: 'number',
        };

        return (
            inputDocumentTypehMap[documentTypeId] ??
            inputDocumentTypehMap[documentTypeId?.toUpperCase()] ??
            InputType.ALPHANUMERIC
        );
    };

    const handleValidateNitDocumentNumberChange = (
        e: React.ChangeEvent<HTMLInputElement>,
        documentTypeId: string,
        setFieldValue: (field: string, value: unknown) => void,
        documentNumberFieldName: string,
        verificationDigitFieldName: string
    ): void => {
        const value = e.target.value;
        const isNitValid = NITValidationRegex.test(value) && value.length === 9;
        if (documentTypeId === TypesDocumentIds.NIT && value.length === 9 && isNitValid) {
            const verificationDigit = VerificationDigitCalculator.calculateVerificationDigit(value);
            setFieldValue(verificationDigitFieldName, verificationDigit);
        } else {
            setFieldValue(verificationDigitFieldName, '');
        }
        setFieldValue(documentNumberFieldName, value);
    };

    return {
        validateDocumentNumber,
        getMaxLengthDocumentByType,
        getInputDocumentTypeByDocumentTypeId,
        handleValidateNitDocumentNumberChange,
    };
};
